import { Link, usePage } from '@inertiajs/react';

import Breadcrumbs from '@/components/public/ui/Breadcrumbs';
import Icon from '@/components/public/ui/Icon';
import Seo from '@/components/public/Seo';
import { parishName, useSite } from '@/hooks/useSite';

type Props = {
  referenceCode?: string;
  [key: string]: unknown;
};

export default function WhistleblowingSubmitted() {
  const { referenceCode } = usePage<Props>().props;
  const site = useSite();
  const name = parishName(site);
  const hq = site.locations.find((l) => l.headquarters) ?? site.locations[0];

  return (
    <>
      <Seo
        title="Denúncia submetida"
        description="A sua denúncia foi recebida. Guarde o código de referência para acompanhamento."
      />
      <div className="mx-auto max-w-4xl px-4 pt-[15vh] pb-16 md:px-8">
        <Breadcrumbs
          items={[
            { label: 'Início', href: '/' },
            { label: 'Canal de Denúncias', href: '/transparency/whistleblowing' },
            { label: 'Submetida' },
          ]}
        />
        <div className="surface-card p-8 md:p-12">
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="bg-tertiary/15 text-tertiary flex h-16 w-16 items-center justify-center rounded-full">
              <Icon name="check_circle" size="lg" />
            </div>
            <div>
              <h1 className="font-headline text-on-surface text-2xl font-bold md:text-3xl">
                Denúncia submetida com sucesso
              </h1>
              <p className="text-on-surface-light mt-2 text-sm">
                A sua submissão foi registada e será analisada pela pessoa ou equipa responsável
                pelo tratamento do canal de denúncias.
              </p>
            </div>
          </div>

          {referenceCode ? (
            <div className="mt-8">
              <p className="text-on-surface-light mb-2 text-center text-xs font-bold tracking-widest uppercase">
                Código de referência
              </p>
              <div className="border-primary/20 bg-primary/[0.06] flex flex-col items-center gap-1 rounded-2xl border p-6">
                <span className="font-headline text-primary text-2xl font-extrabold tracking-wider md:text-3xl">
                  {referenceCode}
                </span>
                <span className="text-on-surface-light text-xs">
                  Guarde este código num local seguro
                </span>
              </div>
            </div>
          ) : (
            <div className="bg-error/[0.06] border-error/20 mt-8 rounded-xl border p-4 text-sm">
              <p className="text-on-surface">
                Não foi possível recuperar o código de referência desta sessão. Por favor entre em
                contacto com a Junta para confirmar a receção.
              </p>
            </div>
          )}

          <div className="mt-8 space-y-4 text-sm">
            <h2 className="font-headline text-on-surface text-base font-bold">Próximos passos</h2>
            <ul className="text-on-surface-light ml-5 list-outside list-disc space-y-2">
              <li>
                Se tiver indicado contacto, receberá acusação de receção no prazo legal aplicável.
              </li>
              <li>
                A pessoa ou equipa responsável comunicará o seguimento dado à denúncia no prazo
                legal aplicável, em regra até <strong className="text-on-surface">3 meses</strong>{' '}
                após a receção da denúncia.
              </li>
              <li>Se indicou email, as comunicações serão efetuadas por essa via.</li>
              <li>
                A sua identidade é tratada de forma confidencial. Quando estejam preenchidos os
                pressupostos legais, beneficia das proteções previstas na Lei n.º 93/2021, incluindo
                a proibição de retaliação.
              </li>
            </ul>
          </div>

          <div className="border-line bg-background mt-6 space-y-3 rounded-xl border p-5 text-sm">
            <h2 className="font-headline text-on-surface text-base font-bold">
              Contactos sobre esta denúncia
            </h2>
            <p className="text-on-surface-light">
              De momento <strong className="text-on-surface">não existe portal de consulta</strong>{' '}
              do estado da denúncia. Para enviar informação adicional ou pedir esclarecimentos,
              mencione o código de referência através de:
            </p>
            <ul className="text-on-surface-light ml-5 list-outside list-disc space-y-1.5">
              <li>
                <strong className="text-on-surface">Correio postal:</strong> {name} — Canal de
                Denúncias{hq?.address ? ` — ${hq.address}` : ''}.
              </li>
              <li>
                <strong className="text-on-surface">Reunião presencial:</strong> mediante pedido
                prévio, com agendamento no prazo máximo de 15 dias.
              </li>
              <li>
                <strong className="text-on-surface">Correio eletrónico:</strong>{' '}
                <span className="text-on-surface-light/70">
                  {site.contactEmail ?? 'a indicar brevemente'}
                </span>
                .
              </li>
            </ul>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Link
              href="/transparency/whistleblowing"
              className="text-primary text-sm font-medium hover:underline"
            >
              ← Voltar ao Canal de Denúncias
            </Link>
            <Link
              href="/"
              className="text-on-surface-light hover:text-on-surface text-sm font-medium"
            >
              Ir para a página inicial
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
